import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import AddLine from '@/assets/icons/ri_add_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import Checkbox from '@/components/atoms/Checkbox';
import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import SomethingHappened from '@/components/SomethingHappened';
import type { SiteTimelineScope } from '@/domain/fishGroups';

import { DashboardViewSwitcher } from '../components/DashboardViewSwitcher/DashboardViewSwitcher';

import BoardEmptyState from './components/BoardEmptyState/BoardEmptyState';
import BoardLegend from './components/BoardLegend/BoardLegend';
import CycleBanner, { UnassignedBanner } from './components/CycleBanner/CycleBanner';
import CycleSelector from './components/CycleSelector/CycleSelector';
import Glyph from './components/Glyph/Glyph';
import TimelineBoard from './components/TimelineBoard/TimelineBoard';
import type { BoardModel, LineageNotice, OriginOutcome } from './hooks/useBoardModel';
import type { BoardCycle, EmptyPen } from './types';

import styles from './FishGroupBoard.module.scss';

export interface FishGroupBoardViewProps {
    /** Display name; masked in demo mode. */
    locationName: string;
    /** Planned and active cycles, then completed ones, which are read-only. */
    cycles: BoardCycle[];
    unassignedCount: number;
    selectedScope: SiteTimelineScope | null;
    /** The selected cycle, or null for no selection and for the unassigned scope. */
    selectedCycle: BoardCycle | null;
    loading: boolean;
    error: unknown;
    /** Null while the selected scope's timeline loads. */
    board: BoardModel | null;
    emptyPens: EmptyPen[];
    showPrevious: boolean;
    expandedBlocks: ReadonlySet<string>;
    onShowPreviousChange: (value: boolean) => void;
    onToggleBlock: (blockKey: string) => void;
    /** Rows whose Origin hid their unshared earlier rows, and the row whose lineage is lit. */
    collapsedRows?: ReadonlySet<number>;
    highlightedRow?: number | null;
    /** An Origin press; without it a press flips the whole block. */
    onToggleOrigin?: (blockKey: string, rowKey: number) => OriginOutcome | null;
    /** The last lineage change, for the board's live region. */
    lineageNotice?: LineageNotice | null;
    /** Groups that continue each other in the same pen share one row. */
    combineChains?: boolean;
    onCombineChainsChange?: (value: boolean) => void;
    onSelectScope: (scope: SiteTimelineScope) => void;
    onNewStocking: () => void;
    onNewFishGroup: (cageId: number | null) => void;
    /** Opens a group's dialog, from its name or its lane. */
    onOpenGroup?: (groupId: number) => void;
    /** Opens Edit stocking from the selected stocking's banner title. */
    onEditStocking?: (stockingCycleId: number) => void;
    /** Demo mode: every create entry point is shown disabled with an explanation. */
    writesDisabled?: boolean;
}

export default function FishGroupBoardView(props: FishGroupBoardViewProps) {
    const { locationName, cycles, unassignedCount, selectedScope, selectedCycle } = props;
    const { loading, error, board, emptyPens, showPrevious, expandedBlocks } = props;
    const { onShowPreviousChange, onToggleBlock, onSelectScope, onNewStocking, onNewFishGroup } =
        props;
    const {
        writesDisabled = false,
        onOpenGroup,
        onEditStocking,
        combineChains = false,
        onCombineChainsChange,
    } = props;
    const { collapsedRows, highlightedRow = null, onToggleOrigin, lineageNotice } = props;
    const { t } = useTranslation('fishGroups');
    const hasGroups = (board?.blocks.length ?? 0) > 0;
    // A customer's new group must go into a planned or active cycle (D21).
    const canCreateGroups = cycles.some((cycle) => cycle.status !== 'completed');
    // A completed cycle is history: its empty pens aren't offered for it.
    const offersEmptyPens = canCreateGroups && selectedCycle?.status !== 'completed';
    const disabledReason = writesDisabled ? t('board.actions.notInDemoMode') : null;
    // Disabled buttons can't take focus, so the reason is also a visible note they point at.
    const demoNoteId = useId();
    const describedBy = writesDisabled ? demoNoteId : undefined;

    const toolbar = (
        <div className={styles.toolbar}>
            <CycleSelector
                cycles={cycles}
                unassignedCount={unassignedCount}
                selected={selectedScope}
                onSelect={onSelectScope}
                onNewStocking={onNewStocking}
                newStockingDisabledReason={disabledReason}
                describedBy={describedBy}
            />
            {writesDisabled && (
                <p id={demoNoteId} className={styles.demoNote}>
                    {t('board.actions.demoModeNote')}
                </p>
            )}
            <div className={styles.toolbarEnd}>
                {hasGroups && (
                    <Checkbox
                        checked={showPrevious}
                        onCheckedChange={onShowPreviousChange}
                        label={t('board.actions.showPrevious')}
                        labelClassName={styles.historyToggle}
                    />
                )}
                {hasGroups && onCombineChainsChange && (
                    <Checkbox
                        checked={combineChains}
                        onCheckedChange={onCombineChainsChange}
                        label={t('board.actions.combineChains')}
                        labelClassName={styles.historyToggle}
                    />
                )}
                <Button
                    variant="secondary"
                    icon={<Glyph icon={AddLine} />}
                    disabled={!canCreateGroups || writesDisabled}
                    tooltip={disabledReason ?? undefined}
                    aria-describedby={describedBy}
                    onClick={() => onNewFishGroup(null)}>
                    {t('board.actions.newFishGroup')}
                </Button>
            </div>
        </div>
    );

    let content = (
        <BoardEmptyState
            title={t('board.empty.noCycleTitle')}
            description={t('board.empty.noCycleDescription')}
            actionLabel={t('board.actions.newStocking')}
            onAction={onNewStocking}
            actionDisabledReason={disabledReason}
            actionDescribedBy={describedBy}
        />
    );
    if (selectedScope !== null && board && !hasGroups) {
        content = (
            <BoardEmptyState
                title={t('board.empty.noGroupsTitle')}
                description={t('board.empty.noGroupsDescription')}
                actionLabel={t('board.actions.newFishGroup')}
                onAction={() => onNewFishGroup(null)}
                actionDisabledReason={disabledReason}
                actionDescribedBy={describedBy}
            />
        );
    } else if (selectedScope !== null && board) {
        content = (
            <>
                <TimelineBoard
                    viewKey={String(selectedScope)}
                    axis={board.axis}
                    blocks={board.blocks}
                    todayPct={board.todayPct}
                    expandedBlocks={expandedBlocks}
                    onToggleBlock={onToggleBlock}
                    collapsedRows={collapsedRows}
                    highlightedRow={highlightedRow}
                    onToggleOrigin={onToggleOrigin}
                    lineageNotice={lineageNotice}
                    emptyPens={offersEmptyPens ? emptyPens : []}
                    onNewFishGroup={onNewFishGroup}
                    createDisabledReason={disabledReason}
                    createDescribedBy={describedBy}
                    onOpenGroup={onOpenGroup}
                />
                <BoardLegend />
            </>
        );
    }

    return (
        <PageLayout
            title={locationName}
            subtitle={t('board.title')}
            width="wide"
            actions={<DashboardViewSwitcher />}>
            {toolbar}
            {loading ? (
                <Spinner size="lg" />
            ) : error ? (
                <SomethingHappened />
            ) : (
                <>
                    {selectedCycle && (
                        <CycleBanner
                            cycle={selectedCycle}
                            lastClosure={board?.lastClosure ?? null}
                            onEdit={
                                onEditStocking && !writesDisabled
                                    ? () => onEditStocking(selectedCycle.id)
                                    : undefined
                            }
                        />
                    )}
                    {selectedScope === 'unassigned' && <UnassignedBanner />}
                    {selectedScope === null || board ? content : null}
                </>
            )}
        </PageLayout>
    );
}
